/**
 * D454: the two trees. The Almanac (the year's upgrades) has four branches, production, tapping,
 * hands and festival; the permanent tree (festival perks, bought with Heritage) has four, start,
 * taps, growth and land. Each branch is a stack of tiers. Any open step of an open tier can be
 * bought, in any order. A fork is two steps side by side in a tier: taking one closes the other
 * until the world is passed on.
 *
 * - An Almanac tier opens once `tree.opensShare` of the tier before it is bought this year,
 *   rounded up: two of three, three of six. The year's upgrades reset at the festival, so the
 *   tiers fill again each year. A step is open once its tier is and what else it needs (its
 *   producer, festivals, a perk, the year's hours, a count owned) is in place.
 * - A permanent tier opens once the Heritage spent in its branch, counted from the levels owned,
 *   reaches `tree.perkTiers[branch][tier - 1]`. A perk already owned can always level up, so a
 *   world's perks are never stranded behind a tier.
 * - D457: each generation opens one more Almanac tier per branch: `tree.firstTiers[branch]` in
 *   the first, up to the branch's last. A tier past the generation's is not shown and cannot open.
 * - D457: a mastery step sits at the foot of each Almanac branch. It opens once every step of the
 *   generation's last tier is bought this year (a fork's other side counts, being closed), and
 *   levels with no top at a price that grows each level. Its levels reset with the year.
 * - An Almanac fork side taken is kept in `permanent.forks` for the generation, so it holds
 *   through festivals. A perk fork needs no record: perks reset when the world is passed on.
 */
import type {
  IdleAlmanacBranchT,
  IdleContent,
  IdleHeritageBranchT,
  IdlePerkT,
  IdleUpgradeT,
} from '@hedgerow/data';
import { IDLE_ALMANAC_BRANCHES, IDLE_HERITAGE_BRANCHES } from '@hedgerow/data';
import {
  idlePerkAtTop,
  idlePerkCost,
  idlePerkFestivalsNeeded,
  idlePerkLevel,
  idlePerkNeedsPerk,
} from './festival.ts';
import type { IdleWorldState } from './state.ts';

/** The generation the world is in: 1 until it is first passed on. */
function generationOf(state: IdleWorldState): number {
  return state.permanent.lineage?.generation ?? 1;
}

/** Festivals held in this generation (as `idleFestivalsThisGeneration`). */
function festivalsOf(state: IdleWorldState): number {
  return state.permanent.festivals.length - (state.permanent.lineage?.festivalsBefore ?? 0);
}

/** Steps a tier of `size` needs bought before the next tier opens: two of three, three of six. */
export function idleTierNeed(content: IdleContent, size: number): number {
  return Math.ceil(size * content.tree.opensShare);
}

/** Each Almanac branch's tiers, `[tier - 1]`, each its steps in content order; one per content. */
const tierIndex = new WeakMap<IdleContent, Map<string, IdleUpgradeT[][]>>();

function tiersOf(content: IdleContent, branch: IdleAlmanacBranchT): IdleUpgradeT[][] {
  let index = tierIndex.get(content);
  if (!index) {
    index = new Map();
    for (const u of content.upgrades) {
      const tiers = index.get(u.branch) ?? [];
      (tiers[u.tier - 1] ??= []).push(u);
      index.set(u.branch, tiers);
    }
    tierIndex.set(content, index);
  }
  return index.get(branch) ?? [];
}

/** D457: the last Almanac tier of a branch open to a generation. */
export function idleGenerationTiers(
  content: IdleContent,
  branch: IdleAlmanacBranchT,
  generation: number,
): number {
  const top = tiersOf(content, branch).length;
  return Math.min(top, (content.tree.firstTiers[branch] ?? top) + Math.max(0, generation - 1));
}

/** D457: the generation that opens a tier of a branch: 1 for the first generation's tiers. */
export function idleTierGeneration(
  content: IdleContent,
  branch: IdleAlmanacBranchT,
  tier: number,
): number {
  return Math.max(1, tier - (content.tree.firstTiers[branch] ?? tier) + 1);
}

/** The Almanac steps of one tier of a branch, in content order. */
export function idleTierSteps(
  content: IdleContent,
  branch: IdleAlmanacBranchT,
  tier: number,
): IdleUpgradeT[] {
  return tiersOf(content, branch)[tier - 1] ?? [];
}

/**
 * A branch's progress this year: steps bought in each tier (`[tier - 1]`), the highest tier
 * open, and, when a tier is shut, the tier before it with what it needs and has. Kept for each
 * `year.upgrades` list, which a purchase replaces rather than changes.
 */
interface BranchProgress {
  bought: number[];
  open: number;
  shut?: { tier: number; need: number; have: number };
  /** D457: the last tier open to this generation. */
  top: number;
}
const progressCache = new WeakMap<string[], WeakMap<IdleContent, Map<string, BranchProgress>>>();

function progressOf(
  state: IdleWorldState,
  content: IdleContent,
  branch: IdleAlmanacBranchT,
): BranchProgress {
  const list = state.year.upgrades;
  let byContent = progressCache.get(list);
  if (!byContent) progressCache.set(list, (byContent = new WeakMap()));
  let byBranch = byContent.get(content);
  if (!byBranch) byContent.set(content, (byBranch = new Map()));
  const top = idleGenerationTiers(content, branch, generationOf(state));
  const key = `${branch}@${top}`;
  const hit = byBranch.get(key);
  if (hit) return hit;
  const bought = new Set(list);
  const tiers = tiersOf(content, branch);
  const p: BranchProgress = { bought: [], open: tiers.length === 0 ? 0 : 1, top };
  tiers.forEach((steps, i) => {
    const have = steps.filter((u) => bought.has(u.id)).length;
    p.bought.push(have);
    const need = idleTierNeed(content, steps.length);
    if (p.open === i + 1 && i + 1 < top) {
      if (have >= need) p.open = i + 2;
      else p.shut = { tier: i + 1, need, have };
    }
  });
  byBranch.set(key, p);
  return p;
}

/** True when an Almanac tier is open: the first always, each after once the one before it is. */
export function idleTierOpen(
  state: IdleWorldState,
  content: IdleContent,
  branch: IdleAlmanacBranchT,
  tier: number,
): boolean {
  return tier <= progressOf(state, content, branch).open;
}

/** The Almanac fork sides taken this generation, by fork id. Empty in a new generation. */
export function idleForksChosen(state: IdleWorldState): Record<string, string> {
  const f = state.permanent.forks;
  return f && f.generation === generationOf(state) ? f.chosen : {};
}

/**
 * The side of this step's fork that closes it: the other side, taken this generation or bought
 * this year. Undefined when it is not a fork side or nothing closes it.
 */
export function idleForkClosedBy(
  state: IdleWorldState,
  content: IdleContent,
  u: IdleUpgradeT,
): string | undefined {
  if (u.choice === undefined || state.year.upgrades.includes(u.id)) return undefined;
  const chosen = idleForksChosen(state)[u.choice];
  if (chosen !== undefined && chosen !== u.id) return chosen;
  return idleTierSteps(content, u.branch, u.tier).find(
    (o) => o.id !== u.id && o.choice === u.choice && state.year.upgrades.includes(o.id),
  )?.id;
}

/**
 * What holds an Almanac step back, for the screen's locked line. `tier` is set while its tier is
 * shut: the tier before, how many of it it needs bought and how many are. The rest is what the
 * step itself needs: its producer owned, festivals still to hold this generation, a perk, hours
 * the year must still run, a count owned.
 */
export interface IdleStepNeeds {
  tier?: { tier: number; need: number; have: number };
  /** D457: the generation that opens its tier, while the world is in an earlier one. */
  generation?: number;
  producer?: string;
  festivals?: number;
  perk?: string;
  yearMs?: number;
  owned?: { producer: string; count: number };
}

/** What holds an Almanac step back, or undefined when nothing does (bought or closed apart). */
export function idleStepNeeds(
  state: IdleWorldState,
  content: IdleContent,
  u: IdleUpgradeT,
): IdleStepNeeds | undefined {
  const y = state.year;
  const needs: IdleStepNeeds = {};
  const p = progressOf(state, content, u.branch);
  if (u.tier > p.top) needs.generation = idleTierGeneration(content, u.branch, u.tier);
  else if (u.tier > p.open && p.shut) needs.tier = p.shut;
  if (u.needsProducer !== undefined && !y.producers[u.needsProducer])
    needs.producer = u.needsProducer;
  const festivals = (u.needsFestivals ?? 0) - festivalsOf(state);
  if (festivals > 0) needs.festivals = festivals;
  if (u.needsPerk !== undefined && !state.permanent.perks[u.needsPerk]) needs.perk = u.needsPerk;
  if (u.needsYearHours !== undefined) {
    const left = u.needsYearHours * 3_600_000 - (state.advancedTo - y.startedAt);
    if (left > 0) needs.yearMs = left;
  }
  if (
    u.needsOwned !== undefined &&
    (y.producers[u.needsOwned.producer]?.owned ?? 0) < u.needsOwned.count
  )
    needs.owned = u.needsOwned;
  return Object.keys(needs).length > 0 ? needs : undefined;
}

/**
 * bought: bought this year. open: can be bought now, coins apart. locked: its tier is shut or it
 * needs something (`needs`). closed: the other side of its fork was taken (`closedBy`).
 */
export type IdleStepState = 'bought' | 'open' | 'locked' | 'closed';

export interface IdleStepView {
  id: string;
  state: IdleStepState;
  /** The fork it is a side of. */
  fork?: string;
  needs?: IdleStepNeeds;
  closedBy?: string;
}

/** One step's state in the year's tree. */
export function idleStepView(
  state: IdleWorldState,
  content: IdleContent,
  u: IdleUpgradeT,
): IdleStepView {
  const base: IdleStepView = { id: u.id, state: 'open' };
  if (u.choice !== undefined) base.fork = u.choice;
  if (state.year.upgrades.includes(u.id)) return { ...base, state: 'bought' };
  const closedBy = idleForkClosedBy(state, content, u);
  if (closedBy !== undefined) return { ...base, state: 'closed', closedBy };
  const needs = idleStepNeeds(state, content, u);
  return needs ? { ...base, state: 'locked', needs } : base;
}

export interface IdleTierView {
  tier: number;
  open: boolean;
  /** Steps bought this year, and the tier's size. */
  bought: number;
  size: number;
  /** Bought steps this tier needs before the next one opens. */
  need: number;
  /** Shut tiers only: the tier before and its progress towards opening this one. */
  opensAfter?: { tier: number; need: number; have: number };
  steps: IdleStepView[];
}

export interface IdleBranchView<B extends string> {
  branch: B;
  /** Steps bought (an Almanac branch, this year) or perks owned (a permanent one), and all. */
  bought: number;
  total: number;
  tiers: IdleTierView[];
}

/**
 * D457: an Almanac branch's mastery step. `open` once every step of the generation's last tier
 * (`tier`) is bought or closed: `done` of `size`. `level` is the levels bought this year; each
 * gives `stat` times `factor` once more.
 */
export interface IdleMasteryView {
  level: number;
  open: boolean;
  tier: number;
  done: number;
  size: number;
  stat: IdleMasteryStat;
  factor: number;
}

export type IdleMasteryStat = 'speed' | 'value' | 'tap' | 'hand';

/** D457: an Almanac branch: its tiers open to this generation, its mastery and the tier to come. */
export interface IdleAlmanacBranchView extends IdleBranchView<IdleAlmanacBranchT> {
  mastery: IdleMasteryView;
  /** The next tier and the generation that opens it; absent once every tier is open. */
  next?: { tier: number; generation: number };
}

/** The year's tree, branch by branch and tier by tier, for the Almanac screen (D454, D457). */
export function idleAlmanacTree(
  state: IdleWorldState,
  content: IdleContent,
): IdleAlmanacBranchView[] {
  return IDLE_ALMANAC_BRANCHES.map((branch) => {
    const p = progressOf(state, content, branch);
    const all = tiersOf(content, branch);
    const tiers: IdleTierView[] = all.slice(0, p.top).map((steps, i) => {
      const view: IdleTierView = {
        tier: i + 1,
        open: i + 1 <= p.open,
        bought: p.bought[i] ?? 0,
        size: steps.length,
        need: idleTierNeed(content, steps.length),
        steps: steps.map((u) => idleStepView(state, content, u)),
      };
      if (!view.open) {
        const before = tiersOf(content, branch)[i - 1]!;
        view.opensAfter = {
          tier: i,
          need: idleTierNeed(content, before.length),
          have: p.bought[i - 1] ?? 0,
        };
      }
      return view;
    });
    const view: IdleAlmanacBranchView = {
      branch,
      bought: p.bought.slice(0, p.top).reduce((a, n) => a + n, 0),
      total: tiers.reduce((a, t) => a + t.size, 0),
      tiers,
      mastery: idleMasteryView(state, content, branch),
    };
    if (p.top < all.length)
      view.next = { tier: p.top + 1, generation: idleTierGeneration(content, branch, p.top + 1) };
    return view;
  });
}

// ---------------------------------------------------------------------------------------
// D457: the mastery step.

/** Mastery levels bought this year in a branch. */
export function idleMasteryLevel(state: IdleWorldState, branch: string): number {
  return state.year.mastery?.[branch] ?? 0;
}

/** The generation's last tier of a branch, which its mastery step waits on. */
export function idleMasteryTier(
  state: IdleWorldState,
  content: IdleContent,
  branch: IdleAlmanacBranchT,
): IdleUpgradeT[] {
  return tiersOf(content, branch)[progressOf(state, content, branch).top - 1] ?? [];
}

/** A branch's mastery step: open once every step of its last open tier is bought or closed. */
export function idleMasteryView(
  state: IdleWorldState,
  content: IdleContent,
  branch: IdleAlmanacBranchT,
): IdleMasteryView {
  const steps = idleMasteryTier(state, content, branch);
  const done = steps.filter(
    (u) => state.year.upgrades.includes(u.id) || idleForkClosedBy(state, content, u) !== undefined,
  ).length;
  const lift = content.tree.mastery.effects[branch];
  return {
    level: idleMasteryLevel(state, branch),
    open: steps.length > 0 && done === steps.length,
    tier: progressOf(state, content, branch).top,
    done,
    size: steps.length,
    stat: lift?.stat ?? 'value',
    factor: lift?.factor ?? 1,
  };
}

/** True when an Almanac step can be bought now, coins apart. */
export function idleStepOpen(
  state: IdleWorldState,
  content: IdleContent,
  u: IdleUpgradeT,
): boolean {
  return idleStepView(state, content, u).state === 'open';
}

/** Record the fork side bought, for the rest of the generation. The same state when not a fork. */
export function idleTakeFork(state: IdleWorldState, u: IdleUpgradeT): IdleWorldState {
  if (u.choice === undefined) return state;
  const chosen = { ...idleForksChosen(state), [u.choice]: u.id };
  return {
    ...state,
    permanent: { ...state.permanent, forks: { generation: generationOf(state), chosen } },
  };
}

// ---------------------------------------------------------------------------------------
// The permanent tree.

/**
 * Heritage spent in a branch of the permanent tree: the price of every level owned there, at
 * today's prices. It opens the branch's tiers.
 */
export function idleHeritageSpent(
  state: IdleWorldState,
  content: IdleContent,
  branch: IdleHeritageBranchT,
): number {
  let sum = 0;
  for (const k of content.perks) {
    if (k.branch !== branch) continue;
    const level = idlePerkLevel(state, k.id);
    for (let l = 0; l < level; l++) sum += idlePerkCost(k, l);
  }
  return sum;
}

/** The Heritage spent in a branch that opens a tier of it: 0 for the first. */
export function idlePerkTierAt(
  content: IdleContent,
  branch: IdleHeritageBranchT,
  tier: number,
): number {
  return content.tree.perkTiers[branch]?.[tier - 1] ?? Infinity;
}

/**
 * Heritage still to spend in the perk's branch before its tier opens: 0 once it is open, and
 * always 0 for a perk owned already, which can level up whatever its tier.
 */
export function idlePerkTierShort(
  state: IdleWorldState,
  content: IdleContent,
  perk: IdlePerkT,
): number {
  if (idlePerkLevel(state, perk.id) > 0) return 0;
  const at = idlePerkTierAt(content, perk.branch, perk.tier);
  return Math.max(0, at - idleHeritageSpent(state, content, perk.branch));
}

/** The other side of a perk's fork, when it is owned and this one is not. */
export function idlePerkClosedBy(
  state: IdleWorldState,
  content: IdleContent,
  perk: IdlePerkT,
): string | undefined {
  if (perk.choice === undefined || idlePerkLevel(state, perk.id) > 0) return undefined;
  return content.perks.find(
    (o) => o.id !== perk.id && o.choice === perk.choice && idlePerkLevel(state, o.id) > 0,
  )?.id;
}

/**
 * What holds a perk's first level back: Heritage still to spend in its branch (`tier`: the
 * Heritage that opens it and the Heritage spent there now), festivals still to hold this
 * generation, or the perk to own first.
 */
export interface IdlePerkNeeds {
  tier?: { tier: number; at: number; spent: number };
  festivals?: number;
  perk?: string;
}

/**
 * bought: a level is owned (`atTop` says whether more can be bought). open: the first level can
 * be bought now, Heritage apart. locked: `needs` says what opens it. closed: the other side of
 * its fork is owned (`closedBy`) until the world is passed on.
 */
export interface IdlePerkView {
  id: string;
  state: IdleStepState;
  level: number;
  atTop: boolean;
  fork?: string;
  needs?: IdlePerkNeeds;
  closedBy?: string;
}

export interface IdlePerkTierView {
  tier: number;
  open: boolean;
  /** Heritage spent in the branch that opens it, and spent there now. */
  at: number;
  spent: number;
  steps: IdlePerkView[];
}

export interface IdlePerkBranchView {
  branch: IdleHeritageBranchT;
  /** Perks with a level owned, and all perks in the branch. */
  bought: number;
  total: number;
  /** Heritage spent in the branch. */
  spent: number;
  tiers: IdlePerkTierView[];
}

/** One perk's state in the permanent tree. */
export function idlePerkView(
  state: IdleWorldState,
  content: IdleContent,
  perk: IdlePerkT,
): IdlePerkView {
  const level = idlePerkLevel(state, perk.id);
  const view: IdlePerkView = {
    id: perk.id,
    state: level > 0 ? 'bought' : 'open',
    level,
    atTop: idlePerkAtTop(state, perk),
  };
  if (perk.choice !== undefined) view.fork = perk.choice;
  if (level > 0) return view;
  const closedBy = idlePerkClosedBy(state, content, perk);
  if (closedBy !== undefined) return { ...view, state: 'closed', closedBy };
  const needs: IdlePerkNeeds = {};
  if (idlePerkTierShort(state, content, perk) > 0)
    needs.tier = {
      tier: perk.tier,
      at: idlePerkTierAt(content, perk.branch, perk.tier),
      spent: idleHeritageSpent(state, content, perk.branch),
    };
  const festivals = idlePerkFestivalsNeeded(state, perk);
  if (festivals > 0) needs.festivals = festivals;
  const first = idlePerkNeedsPerk(state, perk);
  if (first !== undefined) needs.perk = first;
  return Object.keys(needs).length > 0 ? { ...view, state: 'locked', needs } : view;
}

/** The permanent tree, branch by branch and tier by tier, for the Festival screen (D454). */
export function idlePerkTree(state: IdleWorldState, content: IdleContent): IdlePerkBranchView[] {
  return IDLE_HERITAGE_BRANCHES.map((branch) => {
    const on = content.perks.filter((k) => k.branch === branch);
    const spent = idleHeritageSpent(state, content, branch);
    const top = Math.max(0, ...on.map((k) => k.tier));
    const tiers: IdlePerkTierView[] = [];
    for (let t = 1; t <= top; t++) {
      const at = idlePerkTierAt(content, branch, t);
      tiers.push({
        tier: t,
        open: spent >= at,
        at,
        spent,
        steps: on.filter((k) => k.tier === t).map((k) => idlePerkView(state, content, k)),
      });
    }
    const bought = on.filter((k) => idlePerkLevel(state, k.id) > 0).length;
    return { branch, bought, total: on.length, spent, tiers };
  });
}
